import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DollarSign } from "lucide-react";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { 
  RadioGroup, 
  RadioGroupItem 
} from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import { 
  pricingFormSchema, 
  PricingFormValues,
  pricingDefaultValues,
  commonCurrencies
} from "../schemas/pricingFormSchema";

interface PricingFormProps {
  onSectionComplete: (nextSection: string) => void;
}

export const PricingForm = ({ onSectionComplete }: PricingFormProps) => {
  const { toast } = useToast();
  const [showAllOptions, setShowAllOptions] = useState(true);
  const [showCustomCurrency, setShowCustomCurrency] = useState(false);

  // Initialize the pricing form
  const form = useForm<PricingFormValues>({
    resolver: zodResolver(pricingFormSchema),
    defaultValues: pricingDefaultValues,
  });

  const provideEstimate = form.watch("provideEstimate");
  const estimateType = form.watch("estimateType");

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("proposal-pricing");
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData) as PricingFormValues;
        form.reset(parsedData);
        
        // Update UI state based on loaded data
        if (parsedData.priceArrangement) {
          setShowAllOptions(false);
        }
        
        if (parsedData.currency === "other") {
          setShowCustomCurrency(true);
        }
      } catch (error) {
        console.error("Error parsing saved pricing data:", error);
      }
    }
  }, [form]);

  // Form submission handler
  function onSubmit(data: PricingFormValues) {
    console.log("Pricing form submitted:", data);
    // Save the form data to local storage or state management
    localStorage.setItem("proposal-pricing", JSON.stringify(data));
    
    // Show success toast
    toast({
      title: "Pricing saved",
      description: "Moving to the Questions section",
    });
    
    // Navigate to the next section
    onSectionComplete("questions");
  }

  // Handle radio selection to hide other options
  const handlePricingArrangementChange = (value: string) => {
    setShowAllOptions(false);
    form.setValue("priceArrangement", value as any);
  };

  // Reset the pricing form selection and show all options
  const resetPricingSelection = () => {
    setShowAllOptions(true);
    // Set the value to undefined to clear the selection
    form.setValue("priceArrangement", undefined);
    // Reset the form state to its pristine state
    form.clearErrors("priceArrangement");
    // Ensure the form doesn't consider any radio item as checked
    form.setValue("priceArrangement", undefined, { shouldDirty: false });
  };

  // Handle currency selection
  const handleCurrencyChange = (value: string) => {
    form.setValue("currency", value);
    setShowCustomCurrency(value === "other");
    
    if (value !== "other") {
      form.setValue("customCurrency", "");
    }
  };

  // Get the pricing options
  const pricingOptions = [
    { value: "fixed-fee", label: "Fixed Fee" },
    { value: "price-cap", label: "Price Cap" },
    { value: "billable-hour", label: "Billable Hour" },
    { value: "other", label: "Other" },
  ];
  
  // Get the selected option for display when others are hidden
  const selectedOption = pricingOptions.find(
    option => option.value === form.watch("priceArrangement")
  );

  // Get currency symbol
  const selectedCurrency = commonCurrencies.find(c => c.value === form.watch("currency"));
  const currencySymbol = selectedCurrency?.symbol || "$";

  return (
    <div className="transition-opacity">
      <div className="flex items-center mb-4">
        <DollarSign className="h-6 w-6 text-black mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Pricing</h2>
      </div>
      <p className="text-gray-600 mb-6">Define pricing details and options</p>
      
      <Card className="mt-4">
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="priceArrangement"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel>Price Arrangement</FormLabel>
                    <FormDescription>
                      Select the pricing model that best fits your RFP/Instructions.
                    </FormDescription>
                    <FormControl>
                      <RadioGroup
                        onValueChange={handlePricingArrangementChange}
                        value={field.value}
                        className={cn(
                          "grid",
                          showAllOptions ? "gap-3 grid-cols-1 sm:grid-cols-2" : "gap-2"
                        )}
                        showUndo={!showAllOptions}
                        onReset={resetPricingSelection}
                      >
                        {showAllOptions ? (
                          pricingOptions.map((option) => (
                            <FormItem key={option.value} className="flex-1">
                              <FormControl>
                                <RadioGroupItem 
                                  value={option.value}
                                  className="w-full"
                                >
                                  {option.label}
                                </RadioGroupItem>
                              </FormControl>
                            </FormItem>
                          ))
                        ) : (
                          <FormItem>
                            <FormControl>
                              <RadioGroupItem 
                                value={field.value || ""}
                                className="w-full"
                              >
                                {selectedOption?.label}
                              </RadioGroupItem>
                            </FormControl>
                          </FormItem>
                        )}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem className="space-y-3 mt-6">
                    <FormLabel>Currency</FormLabel>
                    <FormDescription>
                      Select the currency in which you would like to be quoted
                    </FormDescription>
                    <Select 
                      onValueChange={handleCurrencyChange} 
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          {commonCurrencies.map((currency) => (
                            <SelectItem key={currency.value} value={currency.value}>
                              {currency.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {showCustomCurrency && (
                <FormField
                  control={form.control}
                  name="customCurrency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Specify Currency</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter currency name (e.g., Brazilian Real)" {...field} />
                      </FormControl>
                      <FormDescription>
                        Please specify the currency you would like to use.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Estimate Price Section */}
              <div className="space-y-4 pt-4 border-t">
                <FormField
                  control={form.control}
                  name="provideEstimate"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel>
                          Would you like to provide an estimate price or range?
                        </FormLabel>
                        <FormDescription>
                          This helps law firms understand your budget expectations
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {provideEstimate && (
                  <div className="space-y-4 rounded-lg bg-muted/50 p-4">
                    <FormField
                      control={form.control}
                      name="estimateType"
                      render={({ field }) => (
                        <FormItem className="space-y-3">
                          <FormLabel>Estimate Type</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              value={field.value}
                              className="flex flex-col space-y-2"
                            >
                              <RadioGroupItem value="fixed" className="w-full">
                                Fixed Price
                              </RadioGroupItem>
                              <RadioGroupItem value="range" className="w-full">
                                Price Range
                              </RadioGroupItem>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {estimateType === "fixed" && (
                      <FormField
                        control={form.control}
                        name="estimatePrice"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Estimated Price ({currencySymbol})</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                min="0"
                                placeholder={`Enter estimated price in ${currencySymbol}`}
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {estimateType === "range" && (
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="estimatePriceMin"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Minimum ({currencySymbol})</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  min="0"
                                  placeholder="Min price"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="estimatePriceMax"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Maximum ({currencySymbol})</FormLabel>
                              <FormControl>
                                <Input 
                                  type="number" 
                                  min="0"
                                  placeholder="Max price"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <Button type="submit">Save & Continue</Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
